import { User } from '@/shared/types';

export interface SessionData {
  user: User;
  token?: string;
  refreshToken?: string;
  redirectUrl?: string;
}

export interface CookieOptions {
  expires?: Date;
  maxAge?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

class SessionService {
  private readonly COOKIE_NAMES = {
    USER_DATA: 'user_data',
    ADMIN_USER_DATA: 'admin_user_data', // Cookie separada para admins
    REDIRECT_URL: 'redirect_url',
  } as const;

  // localStorage keys for tokens (cross-domain compatible — cookies HTTP-only don't work cross-domain)
  private readonly TOKEN_KEYS = {
    AUTH_TOKEN: 'inbo_auth_token',
    REFRESH_TOKEN: 'inbo_refresh_token',
    ADMIN_AUTH_TOKEN: 'inbo_admin_auth_token',
    ADMIN_REFRESH_TOKEN: 'inbo_admin_refresh_token',
  } as const;

  private readonly DEFAULT_COOKIE_OPTIONS: CookieOptions = {
    path: '/',
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax',
    httpOnly: false, // Necesario para acceso desde JavaScript
    maxAge: 7 * 24 * 60 * 60, // 7 días en segundos
  };

  /**
   * Establece una cookie con las opciones especificadas
   */
  private setCookie(
    name: string,
    value: string,
    options: CookieOptions = {}
  ): void {
    try {
      const cookieOptions = { ...this.DEFAULT_COOKIE_OPTIONS, ...options };
      const cookieString = `${name}=${encodeURIComponent(value)}; ${this.formatCookieOptions(cookieOptions)}`;
      document.cookie = cookieString;

      // Verificar que la cookie se estableció correctamente
      const verifyCookie = this.getCookie(name);
      if (verifyCookie === null && value !== '') {
        // La cookie no se persistió (size limit / invalid char / CSP / SameSite policy).
        // Antes este caso se tragaba silenciosamente — ahora lo elevamos.
        const err = new Error(
          `[sessionService.setCookie] Cookie "${name}" no persistida tras document.cookie write`
        );
        console.error(err.message, { name, cookieOptions });
        throw err;
      }
    } catch (error) {
      console.error('[sessionService.setCookie] Failed to write cookie', name, error);
      throw error;
    }
  }

  /**
   * Obtiene el valor de una cookie
   */
  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(c.substring(nameEQ.length, c.length));
        return value;
      }
    }

    return null;
  }

  /**
   * Formatea las opciones de cookie en string
   */
  private formatCookieOptions(options: CookieOptions): string {
    const parts: string[] = [];

    if (options.path) parts.push(`path=${options.path}`);
    if (options.domain) parts.push(`domain=${options.domain}`);
    if (options.maxAge) parts.push(`max-age=${options.maxAge}`);
    if (options.expires) parts.push(`expires=${options.expires.toUTCString()}`);
    if (options.secure) parts.push('secure');
    if (options.httpOnly) parts.push('httponly');
    if (options.sameSite) parts.push(`samesite=${options.sameSite}`);

    return parts.join('; ');
  }

  /**
   * Elimina una cookie
   */
  private deleteCookie(
    name: string,
    options: Partial<CookieOptions> = {}
  ): void {
    const cookieOptions = { ...this.DEFAULT_COOKIE_OPTIONS, ...options };
    this.setCookie(name, '', {
      ...cookieOptions,
      expires: new Date(0),
      maxAge: 0,
    });
  }

  /**
   * Guarda los datos de sesión en cookies
   */
  saveSession(sessionData: SessionData, isAdmin: boolean = false): void {
    try {

      // Determinar qué cookie usar según el tipo de sesión
      const cookieName = isAdmin
        ? this.COOKIE_NAMES.ADMIN_USER_DATA
        : this.COOKIE_NAMES.USER_DATA;

      // Guardar datos del usuario en cookie (accesible desde JS)
      if (sessionData.user && typeof sessionData.user === 'object') {
        const userDataString = JSON.stringify(sessionData.user);
        this.setCookie(cookieName, userDataString, {
          httpOnly: false, // Necesario para acceso desde JS
          maxAge: 7 * 24 * 60 * 60, // 7 días
        });
      } else {
        this.deleteCookie(cookieName);
      }

      // Guardar tokens en localStorage (cross-domain compatible)
      if (sessionData.token) {
        const tokenKey = isAdmin ? this.TOKEN_KEYS.ADMIN_AUTH_TOKEN : this.TOKEN_KEYS.AUTH_TOKEN;
        localStorage.setItem(tokenKey, sessionData.token);
      }
      if (sessionData.refreshToken) {
        const refreshKey = isAdmin ? this.TOKEN_KEYS.ADMIN_REFRESH_TOKEN : this.TOKEN_KEYS.REFRESH_TOKEN;
        localStorage.setItem(refreshKey, sessionData.refreshToken);
      }

      // Guardar URL de redirección si existe
      if (sessionData.redirectUrl) {
        this.setCookie(
          this.COOKIE_NAMES.REDIRECT_URL,
          sessionData.redirectUrl,
          {
            httpOnly: false,
            maxAge: 60 * 60, // 1 hora
          }
        );
      }

    } catch (error) {
      throw new Error('No se pudo guardar la sesión');
    }
  }

  /**
   * Restaura los datos de sesión desde cookies
   */
  restoreSession(isAdmin: boolean = false): SessionData | null {
    try {
      // Determinar qué cookie leer según el tipo de sesión
      const cookieName = isAdmin
        ? this.COOKIE_NAMES.ADMIN_USER_DATA
        : this.COOKIE_NAMES.USER_DATA;
      const userDataStr = this.getCookie(cookieName);
      const redirectUrl = this.getCookie(this.COOKIE_NAMES.REDIRECT_URL);

      // Con cookies HTTP-only no podemos verificar el token desde el frontend.
      // Mientras exista la cookie de datos del usuario, intentamos restaurar la sesión
      // y delegamos la validación real al backend en cada request protegida.
      if (!userDataStr) {
        return null;
      }

      let user;
      try {
        user = JSON.parse(userDataStr);
      } catch (parseError) {
        // Limpiar cookie corrupta
        this.deleteCookie(cookieName);
        return null;
      }


      // Restore tokens from localStorage
      const tokenKey = isAdmin ? this.TOKEN_KEYS.ADMIN_AUTH_TOKEN : this.TOKEN_KEYS.AUTH_TOKEN;
      const refreshKey = isAdmin ? this.TOKEN_KEYS.ADMIN_REFRESH_TOKEN : this.TOKEN_KEYS.REFRESH_TOKEN;
      const token = localStorage.getItem(tokenKey) || undefined;
      const refreshToken = localStorage.getItem(refreshKey) || undefined;

      return {
        user,
        token,
        refreshToken,
        redirectUrl: redirectUrl || undefined,
      };
    } catch (error) {
      this.clearSession(isAdmin);
      return null;
    }
  }

  /**
   * Obtiene el token de autenticación actual
   * Prioriza admin token si existe, luego regular
   */
  getToken(): string | null {
    try {
      const adminToken = localStorage.getItem(this.TOKEN_KEYS.ADMIN_AUTH_TOKEN);
      if (adminToken) return adminToken;
      return localStorage.getItem(this.TOKEN_KEYS.AUTH_TOKEN);
    } catch {
      return null;
    }
  }

  /**
   * Obtiene el refresh token actual
   * Prioriza admin refresh token si existe, luego regular
   */
  getRefreshToken(): string | null {
    try {
      const adminRefresh = localStorage.getItem(this.TOKEN_KEYS.ADMIN_REFRESH_TOKEN);
      if (adminRefresh) return adminRefresh;
      return localStorage.getItem(this.TOKEN_KEYS.REFRESH_TOKEN);
    } catch {
      return null;
    }
  }

  /**
   * Obtiene los datos del usuario actual
   * Intenta primero con admin_user_data, luego con user_data
   */
  getUser(): User | null {
    try {
      // Primero intentar obtener datos de admin
      let userDataStr = this.getCookie(this.COOKIE_NAMES.ADMIN_USER_DATA);
      // let isAdmin = true;

      // Si no hay datos de admin, intentar con usuario normal
      if (!userDataStr || userDataStr === 'undefined' || userDataStr === 'null') {
        userDataStr = this.getCookie(this.COOKIE_NAMES.USER_DATA);
        // isAdmin = false;
      }

      // Si tampoco hay datos de usuario normal, retornar null
      if (!userDataStr || userDataStr === 'undefined' || userDataStr === 'null') {
        return null;
      }

      return JSON.parse(userDataStr);
    } catch (error) {
      // Limpiar cookies corruptas
      this.deleteCookie(this.COOKIE_NAMES.USER_DATA);
      this.deleteCookie(this.COOKIE_NAMES.ADMIN_USER_DATA);
      return null;
    }
  }

  /**
   * Obtiene la URL de redirección
   */
  getRedirectUrl(): string | null {
    return this.getCookie(this.COOKIE_NAMES.REDIRECT_URL);
  }

  /**
   * Verifica si existe una sesión activa
   */
  hasActiveSession(): boolean {
    const user = this.getUser();
    const hasSession = !!user;

    return hasSession;
  }

  /**
   * Limpia toda la sesión
   * NOTA: Las cookies HTTP-only del backend no se pueden eliminar desde el frontend
   */
  clearSession(isAdmin: boolean = false): void {

    // Determinar qué cookie limpiar según el tipo de sesión
    const cookieName = isAdmin
      ? this.COOKIE_NAMES.ADMIN_USER_DATA
      : this.COOKIE_NAMES.USER_DATA;

    // Limpiar cookies de datos de usuario
    this.deleteCookie(cookieName);
    this.deleteCookie(this.COOKIE_NAMES.REDIRECT_URL);

    // Limpiar tokens de localStorage
    try {
      if (isAdmin) {
        localStorage.removeItem(this.TOKEN_KEYS.ADMIN_AUTH_TOKEN);
        localStorage.removeItem(this.TOKEN_KEYS.ADMIN_REFRESH_TOKEN);
      } else {
        localStorage.removeItem(this.TOKEN_KEYS.AUTH_TOKEN);
        localStorage.removeItem(this.TOKEN_KEYS.REFRESH_TOKEN);
      }
    } catch {
      // localStorage may not be available
    }

  }

  /**
   * Limpia cookies corruptas
   */
  cleanCorruptCookies(): void {
    // Verificar y limpiar user data normal
    const userDataStr = this.getCookie(this.COOKIE_NAMES.USER_DATA);
    if (userDataStr === 'undefined' || userDataStr === 'null') {
      this.deleteCookie(this.COOKIE_NAMES.USER_DATA);
    }

    // Verificar y limpiar admin user data
    const adminUserDataStr = this.getCookie(this.COOKIE_NAMES.ADMIN_USER_DATA);
    if (adminUserDataStr === 'undefined' || adminUserDataStr === 'null') {
      this.deleteCookie(this.COOKIE_NAMES.ADMIN_USER_DATA);
    }
  }

  /**
   * Obtiene la sesión completa
   */
  getSession(): SessionData | null {
    const user = this.getUser();
    if (!user) return null;
    
    return {
      user,
      token: this.getToken() || undefined,
      refreshToken: this.getRefreshToken() || undefined,
    };
  }

  /**
   * Limpia la caché de la sesión
   */
  async clearCache(): Promise<void> {
    this.clearSession();
  }

  /**
   * Obtiene información de debug de las cookies
   */
  getDebugInfo(): Record<string, any> {
    const user = this.getUser();
    return {
      hasUser: !!user,
      hasRedirectUrl: !!this.getRedirectUrl(),
      userRole: user?.role || null,
      httpOnlyTokensManagedByBackend: true,
      cookies: document.cookie,
    };
  }
}

// Instancia singleton del servicio
export const sessionService = new SessionService();
export default sessionService;
